// refusal-toast.ts — why Roll did not go, on a screen too short for the product's own message rail (SC-61819, SC-67337;
// board-style.ts has the CSS and the reasoning). CSS shows exactly one of the two — the rail, or this toast — for the
// viewport's height, so the player reads, and a screen reader announces, one message.
//
// Two things put text here:
//   - a refusal of the wallet (show): the operator's own short phrase (round-outcome.ts), set as text. It leaves on the
//     next attempt (the caller clears it) or after a few seconds;
//   - the rail's own message (rail, fed by followStakeRail): "Not enough balance", "Enter a stake", a refused stake
//     text. On a short screen the product sheds the rail, and Roll was disabled with nothing on screen saying why
//     (SC-67337). It stands for exactly as long as the rail holds it.
// A refusal outranks the rail while it is up; the game writes the same reason into its rail, so when the refusal's
// time is over the rail's copy of it stays, as it does on a taller screen.
const SHOWN_MS = 6_000;

export interface RefusalToast {
  /** `announce: false` shows the reason without announcing it — for a refusal something else already announces (an
   *  autobet run's summary in the game's own live region): one announcement per refusal. */
  show(message: string, opts?: { readonly announce?: boolean }): void;
  /** A new attempt: the refusal goes (the rail's own message stays while the rail holds it). */
  clear(): void;
  /** SC-67337: the stake rail's own message, '' when the rail clears. */
  rail(message: string): void;
}

export function refusalToast(doc: Document): RefusalToast {
  let el: HTMLElement | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let refusal: { text: string; announce: boolean } | null = null;
  let railText = "";
  /** Whether the rail's message is announced here: yes, except for the rail's copy of a refusal that was not announced
   *  (an autobet run's, which the run's summary announced) — it stays unannounced until the rail says something else. */
  let railAnnounce = true;
  /** What the toast says now: the refusal while it is up, else the rail's own message. Nothing is rewritten when that
   *  does not change, so a live region never announces one message twice. */
  const render = (): void => {
    const text = refusal ? refusal.text : railText;
    const announce = refusal ? refusal.announce : railAnnounce;
    if (!el) {
      if (text === "") return; // nothing added to the page until there is something to say
      el = doc.createElement("p");
      el.setAttribute("data-dice-refusal", "");
      doc.body.appendChild(el);
    }
    if (announce) {
      el.removeAttribute("aria-hidden");
      el.setAttribute("role", "status");
    } else {
      el.removeAttribute("role");
      el.setAttribute("aria-hidden", "true");
    }
    if (el.textContent !== text) el.textContent = text;
  };
  const dropRefusal = (): void => {
    clearTimeout(timer);
    timer = undefined;
    if (refusal && refusal.text === railText) railAnnounce = refusal.announce; // the same message, still on the rail
    refusal = null;
  };
  return {
    show(message, opts = {}) {
      clearTimeout(timer);
      refusal = { text: message, announce: opts.announce !== false };
      timer = setTimeout(() => {
        dropRefusal();
        render();
      }, SHOWN_MS);
      render();
    },
    clear() {
      dropRefusal();
      render();
    },
    rail(message) {
      if (message !== railText) railAnnounce = true;
      railText = message;
      render();
    },
  };
}

/** SC-67337: hands the toast the stake rail's own message (#betMsg) — what it holds now, and every change after. The
 *  game is the rail's one writer; this only reads it. Returns the way to stop following it. */
export function followStakeRail(doc: Document, toast: RefusalToast): () => void {
  const rail = doc.getElementById("betMsg");
  if (!rail) return () => {};
  const pass = (): void => toast.rail((rail.textContent ?? "").trim());
  pass();
  const Observer = doc.defaultView?.MutationObserver ?? MutationObserver; // the rail's own window
  const watch = new Observer(pass);
  watch.observe(rail, { childList: true, characterData: true, subtree: true });
  return () => watch.disconnect();
}
